import React, { useEffect } from 'react';
import { GlassCard } from './reports/_shared/GlassCard';

const Icon = ({ path, className = "w-6 h-6" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
);

const CheckIcon = () => <Icon path="M4.5 12.75l6 6 9-13.5" className="w-5 h-5 text-indigo-400" />;

export default function AbhiveerProposal() {
    
    useEffect(() => {
        document.title = 'SocialSEO | Abhiveer Network Scaling Proposal';
    }, []);

    const plans = [
        {
            id: 1,
            videos: 60,
            fanPages: 0,
            monthly: 999,
            colorClass: "border-t-slate-400",
            bgClass: "bg-slate-500",
            textClass: "text-slate-400"
        },
        {
            id: 2,
            videos: 90,
            fanPages: 3,
            monthly: 1499,
            colorClass: "border-t-blue-500",
            bgClass: "bg-blue-500",
            textClass: "text-blue-400"
        },
        {
            id: 3,
            videos: 120,
            fanPages: 3,
            monthly: 1799,
            colorClass: "border-t-indigo-500",
            bgClass: "bg-indigo-500",
            textClass: "text-indigo-400"
        },
        {
            id: 4,
            videos: 150,
            fanPages: 5,
            monthly: 2199,
            colorClass: "border-t-purple-500",
            bgClass: "bg-purple-500",
            textClass: "text-purple-400"
        },
        {
            id: 5,
            videos: 180,
            fanPages: 5,
            monthly: 2499,
            colorClass: "border-t-pink-500",
            bgClass: "bg-pink-500",
            textClass: "text-pink-400"
        }
    ];

    const getDiscountedPrice = (monthly) => {
        return Math.round(monthly * 3 * 0.8).toLocaleString();
    };

    return (
        <div className="min-h-screen bg-transparent font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20">
            <div className="relative z-10">
                {/* Header */}
                <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                    <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                        <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                    </div>
                </header>

                <main className="container mx-auto px-4 pt-32 space-y-24">

                    {/* Hero */}
                    <section className="text-center max-w-4xl mx-auto">
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                            SocialSEO x Abhiveer
                        </h1>
                        <p className="text-xl md:text-2xl text-white/70 mb-10 leading-relaxed max-w-3xl mx-auto">
                            The blueprint to transform 1 podcast into a synchronized, omnipresent distribution network.
                        </p>
                    </section>

                    {/* What's Included */}
                    <section>
                        <GlassCard title="Included in All Plans" className="max-w-4xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ul className="space-y-4">
                                    <li className="flex items-start"><CheckIcon /><span className="ml-3 text-white/80">Full editing, scripting, and hook generation</span></li>
                                    <li className="flex items-start"><CheckIcon /><span className="ml-3 text-white/80">Multiple IP Page Creation (Theme/Topic Pages)</span></li>
                                    <li className="flex items-start"><CheckIcon /><span className="ml-3 text-white/80">Platform distribution (IG, YT Shorts, TikTok Fix)</span></li>
                                </ul>
                                <ul className="space-y-4">
                                    <li className="flex items-start"><CheckIcon /><span className="ml-3 text-white/80">Dedicated Account Manager & Analytics</span></li>
                                    <li className="flex items-start"><CheckIcon /><span className="ml-3 text-white/80">Content recycling strategy from the 90+ backlog</span></li>
                                    <li className="flex items-start"><CheckIcon /><span className="ml-3 text-white/80">Audience diversification routing for brand deals</span></li>
                                </ul>
                            </div>
                        </GlassCard>
                    </section>

                    {/* Plans */}
                    <section id="plans">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-5xl font-bold mb-4">Investment Tiers</h2>
                        </div>
                        
                        {/* 5-Column Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-[1400px] mx-auto">
                            {plans.map((plan, idx) => (
                                <GlassCard key={idx} className={`flex flex-col h-full border-t-4 ${plan.colorClass}`}>
                                    <div className="text-center pb-6 border-b border-white/10 mb-6">
                                        <h3 className="text-2xl font-bold mb-1">Plan {plan.id}</h3>
                                    </div>

                                    <div className="mb-6 text-center">
                                        <span className="text-4xl font-bold">${plan.monthly.toLocaleString()}</span>
                                        <span className="text-white/60">/mo</span>
                                    </div>

                                    <div className="space-y-4 flex-grow mb-8">
                                        <p className="flex items-center text-lg">
                                            <span className={`w-2 h-2 ${plan.bgClass} rounded-full mr-3`}></span>
                                            <span className="font-bold mr-1">{plan.videos}</span> videos/month
                                        </p>
                                        <p className="flex items-center text-lg">
                                            <span className={`w-2 h-2 ${plan.bgClass} rounded-full mr-3`}></span>
                                            <span className="font-bold mr-1">{plan.fanPages + 1}</span> Total Pages
                                        </p>
                                        <p className="text-sm text-white/50 pl-5">
                                            (1 Main + {plan.fanPages} Fan Pages)
                                        </p>
                                    </div>
                                    
                                    {/* The Upfront Kicker Callout */}
                                    <div className="mt-auto bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
                                        <p className="text-xs text-white/60 mb-1 uppercase tracking-wide">3-Mo Upfront Bonus</p>
                                        <p className="text-lg font-bold text-green-400">20% OFF</p>
                                        <p className="text-sm font-semibold mt-1">Total: ${getDiscountedPrice(plan.monthly)}</p>
                                    </div>
                                </GlassCard>
                            ))}
                        </div>
                    </section>

                    {/* Content Output Breakdown Table */}
                    <section className="mb-24">
                        <div className="max-w-[1400px] mx-auto space-y-12">
                            
                            <GlassCard className="overflow-hidden">
                                <div className="p-6 md:p-10 border-b border-white/10 bg-indigo-500/5">
                                    <h2 className="text-2xl md:text-3xl font-bold mb-2 text-indigo-100">Content Distribution Matrix</h2>
                                    <p className="text-indigo-200/60 text-sm">
                                        Detailed breakdown of the post volume generated per month based on your selected tier.
                                    </p>
                                </div>
                                
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-white/5 border-b border-white/10">
                                                <th className="p-4 md:p-6 font-semibold min-w-[100px]">Tier</th>
                                                <th className="p-4 md:p-6 font-semibold min-w-[150px]">Base Output</th>
                                                <th className="p-4 md:p-6 font-semibold min-w-[180px]">Fan Pages (New)</th>
                                                <th className="p-4 md:p-6 font-semibold min-w-[200px]">Fan Pages (Backlog)</th>
                                                <th className="p-4 md:p-6 font-semibold text-indigo-300 min-w-[150px]">Total Network Volume</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/10">
                                            {plans.map((plan, idx) => {
                                                const mainOutput = plan.videos;
                                                const fanNewOutput = plan.videos * plan.fanPages;
                                                const fanBacklogOutput = 60 * plan.fanPages;
                                                const totalVolume = mainOutput + fanNewOutput + fanBacklogOutput;

                                                return (
                                                    <tr key={idx} className="hover:bg-white/5 transition-colors">
                                                        <td className="p-4 md:p-6 font-bold">Plan {plan.id}</td>
                                                        <td className="p-4 md:p-6 text-white/80">
                                                            {mainOutput}
                                                        </td>
                                                        <td className="p-4 md:p-6 text-white/80">
                                                            {plan.fanPages > 0 ? fanNewOutput : "-"}
                                                        </td>
                                                        <td className="p-4 md:p-6 text-white/80">
                                                            {plan.fanPages > 0 ? fanBacklogOutput : "-"}
                                                        </td>
                                                        <td className="p-4 md:p-6 bg-indigo-500/5">
                                                            <span className="text-xl font-bold text-indigo-300">
                                                                {totalVolume.toLocaleString()} posts
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </GlassCard>

                            {/* Estimation & Reality / "The 9x Multiplier" */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Base Stat */}
                                <div className="bg-gradient-to-br from-indigo-900/50 to-indigo-800/20 border border-indigo-500/30 rounded-2xl p-8 flex flex-col justify-center text-center">
                                    <h3 className="text-white/60 uppercase tracking-widest text-sm font-bold mb-4">Month 1 Baseline</h3>
                                    <div className="text-5xl md:text-6xl font-black text-white mb-2">0.8M<span className="text-3xl text-indigo-300">+</span></div>
                                    <p className="text-indigo-200 text-lg font-medium">Views from 60 videos</p>
                                    <p className="text-white/50 text-sm mt-4">The core funnel is warmed up. Organic numbers strictly on the main page will naturally elevate in Month 2 via standard exponential modeling.</p>
                                </div>

                                {/* Multiplier Effect */}
                                <div className="col-span-1 lg:col-span-2 bg-gradient-to-br from-[#0f172a] to-[#020617] border border-white/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
                                    <div className="absolute -right-10 -top-10 text-9xl font-black text-white/5 pointer-events-none">9X</div>
                                    <h3 className="text-3xl font-black mb-6 text-white"><span className="text-indigo-400">The 540-Video</span> Compound Matrix</h3>
                                    
                                    <div className="space-y-6">
                                        <div className="flex items-start gap-4">
                                            <div className="mt-1 bg-indigo-500/20 p-2 rounded-lg text-indigo-400">
                                                <Icon path="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                                            </div>
                                            <div>
                                                <h4 className="text-xl font-bold text-white mb-1">9x The Surface Area</h4>
                                                <p className="text-white/70 leading-relaxed">
                                                    We are aggressively jumping from 60 to over 500+ videos. The new fan pages require an initial algorithm incubation period to index effectively, but the sheer volume essentially guarantees search pattern penetration.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-4">
                                            <div className="mt-1 bg-green-500/20 p-2 rounded-lg text-green-400 flex-shrink-0">
                                                <Icon path="M11.412 15.655L9.75 21.75l3.745-4.012M9.257 13.5H3.75l2.659-2.849m2.048-2.194L14.25 2.25 12 10.5h8.25l-4.707 5.043M8.457 8.457L3 3m5.457 5.457l7.086 7.086m0 0L21 21" />
                                            </div>
                                            <div>
                                                <h4 className="text-xl font-bold text-white mb-1">The High-Floor Advantage</h4>
                                                <p className="text-white/70 leading-relaxed">
                                                    Even in a worst-case scenario where 3 out of 4 new pages completely flop, the extreme volume ensures that the 1 succeeding page will generate immense, sustained traffic. By Month 3, performance compounding makes collective views highly protective and scalable.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}
